import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { LOCALES_IMAGES, COWORKING_IMAGES } from './Locales';
import { useLanguage } from '../contexts/LanguageContext';

function LocaleDetalle() {
  const { t } = useLanguage();
  const { slug } = useParams();
  const [selectedImg, setSelectedImg] = useState(0);

  /* Buscar todas las imágenes que pertenecen a este slug (locales + coworking) */
  const allImages = useMemo(() => {
    const all = [...LOCALES_IMAGES, ...COWORKING_IMAGES];
    return all.filter((img) => img.detailSlug === slug);
  }, [slug]);

  /* Sacar los features del primero que los tenga */
  const features = useMemo(() => {
    const withFeatures = allImages.find((img) => img.features && img.features.length > 0);
    return withFeatures ? withFeatures.features : [];
  }, [allImages]);

  /* Datos del local */
  const first = allImages[0];

  /* Si no encuentra nada, mostrar error */
  if (!first) {
    return (
      <section className="section section--detalle">
        <div className="section-inner detalle-not-found">
          <h1 className="section-title">{t('detalle.notFoundTitle')}</h1>
          <p className="detalle-not-found-text">{t('detalle.notFoundText')}</p>
          <Link to="/locales" className="detalle-back-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M19 12H5" />
              <path d="m12 19-7-7 7-7" />
            </svg>
            {t('detalle.back')}
          </Link>
        </div>
      </section>
    );
  }

  const location = first.location;
  const address = first.overlayText;
  const mapsUrl = first.mapsUrl;
  const isCoworking = first.detailSlug.startsWith('coworking');
  /* La superficie viene dentro de "features" (ej. "120 m²"): se saca de
   * ahí para poder destacarla junto al título. */
  const size = features.find((f) => /m²|m2|sqm/i.test(f));

  return (
    <section className="section section--detalle">
      <div className="section-inner">
        {/* Botón volver */}
        <Link to="/locales" className="detalle-back-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M19 12H5" />
            <path d="m12 19-7-7 7-7" />
          </svg>
          {t('detalle.back')}
        </Link>

        <div className="detalle-header">
          <span className="detalle-type-badge">
            {isCoworking ? t('detalle.coworkingBadge') : t('detalle.localBadge')}
          </span>
          <h1 className="detalle-title">{location}</h1>
          <a
            href={mapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="detalle-address"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {address}
          </a>

          {/* Datos clave a la vista: superficie, disponibilidad y precio */}
          <div className="detalle-meta">
            {size && <span className="chip chip--size">{size}</span>}
            <span className="chip chip--status">
              <span className="status-dot" aria-hidden />
              {t('carousel.available')}
            </span>
            <span className="chip">{t('detalle.priceOnRequest')}</span>
          </div>
        </div>

        {/* Galería principal */}
        <div className="detalle-gallery">
          <div className="detalle-gallery-main">
            <img
              src={allImages[selectedImg].src}
              alt={allImages[selectedImg].alt}
              className="detalle-gallery-main-img"
            />
            <div className="detalle-gallery-counter">
              {selectedImg + 1} / {allImages.length}
            </div>
          </div>
          {allImages.length > 1 && (
            <div className="detalle-gallery-thumbs">
              {allImages.map((img, i) => (
                <button
                  key={img.id}
                  type="button"
                  className={`detalle-thumb ${i === selectedImg ? 'detalle-thumb--active' : ''}`}
                  onClick={() => setSelectedImg(i)}
                  aria-label={`${t('detalle.viewPhoto')} ${i + 1}`}
                >
                  <img src={img.src} alt={img.alt} loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Características */}
        {features.length > 0 && (
          <div className="detalle-features">
            <h2 className="detalle-features-title">{t('detalle.featuresTitle')}</h2>
            <div className="detalle-features-grid">
              {features.map((feat, i) => (
                <div key={i} className="detalle-feature-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  <span>{feat}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="detalle-cta">
          <Link to="/contacto" className="detalle-cta-btn">
            {t('detalle.ctaInfo')}
          </Link>
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="detalle-cta-btn detalle-cta-btn--outline">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {t('detalle.ctaMaps')}
          </a>
        </div>
      </div>
    </section>
  );
}

export default LocaleDetalle;
